import React, { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import {
  useForm,
  useFieldArray,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import {
  useLoaderData,
  useNavigate,
} from '@tanstack/react-router';
import StickyBox from 'react-sticky-box';
import { sortBy } from 'remeda';
import { DragDropProvider } from '@dnd-kit/react';

import type { UppyFile } from '@uppy/core';

import { useStore } from '../store';

import { APIdeleteAsset } from '../api/Assets';
import {
  APIcreateTimeline,
  APIupdateTimeline,
} from '../api/Timeline';

import TimelineCreateMemory from '../components/TimelineCreate/Memory';
import TimelineCreateFiles from '../components/TimelineCreate/Files';
import TimelineCreateHeaderImage from '../components/TimelineCreate/HeaderImage';
import TimelineCreateInfo from '../components/TimelineCreate/Info';
import TimelineButtons from '../components/Timeline/Buttons';
import MemoryTypeSelect from '../components/MemoryForm/TypeSelect';
import MemoryLgcyTypeSelect from '../components/MemoryForm/LgcyTypeSelect';
import MemoryEngagement from '../components/MemoryForm/Engagement';
import TimelineHeader from '../components/Timeline/Header';
import TimelineContributors from '../components/Timeline/Contributors';
import TimelineContributorNotice from '../components/Timeline/ContributorNotice';
import MemoryTimeline from '../components/Memory/Timeline';
import TimelineCreateVariantSelect from '../components/TimelineCreate/VariantSelect';

import type { Asset } from '../types/asset';
import type { Memory } from '../types/memory';
import type { Tag } from '../types/tag';
import type { Timeline } from '../types/timeline';
import type { TimelineForm } from '../types/forms/timelineform';
import type { TimelineMemoryForm } from '../types/forms/timelinememoryform';

const TimelineCreate: React.FC = () => {
  const { timeline } = useLoaderData({ strict: false });
  const { profile } = useStore.getState();
  const nav = useNavigate();

  const [files, setFiles] = useState<UppyFile<Record<string, unknown>, Record<string, unknown>>[]>([]);
  const [memories, setMemories] = useState<TimelineMemoryForm[]>([]);
  const [memoryErrors, setMemoryErrors] = useState<Record<string, any>>({});
  const [additionalAssets, setAdditionalAssets] = useState<Asset[]>([]);
  const [uploading, setUploading] = useState(false);
  const [headerImageUploading, setHeaderImageUploading] = useState(false);
  const [headerImageId, setHeaderImageId] = useState<string | null>(timeline?.header_image?.id ?? null);
  const [hoveredMemoryId, setHoveredMemoryId] = useState<string | null>(null);

  const sourceParentRef = useRef<Element | null>(null);

  const schemaMemory = z.object({
    id: z.string().min(1, { message: "ID is required" }),
    title: z.string().min(1, { message: "Title is required" }),
    name: z.string().min(1, { message: "Name is required" }),
    date: z.string().min(1, { message: "Date is required" }),
    content: z.string().or(z.null()).optional(),
    tags: z.array(z.number().or(z.undefined())).optional(),
    assets: z.array(z.string()).min(1, { message: "At least one asset is required" }),
  });

  const schemaTimeline = z.object({
    type: z.string(),
    lgcy_type: z.string().min(1, { message: "Please select one option" }),
    dedicated_to: z.string().min(1, { message: "This field is required" }),
    allow_comments: z.boolean(),
    allow_reactions: z.boolean(),
    header_image_id: z.string().nullable().optional(),
    memories: z.refine((memories) => {
      setMemoryErrors({});
      let invalid = false;
      if (memories && Object.keys(memories).length > 0) {
        Object.keys(memories).forEach((key) => {
          const memory = (memories as Record<string, TimelineMemoryForm>)[key];
          if (!memory.removed) {
            const result = schemaMemory.safeParse(memory as TimelineMemoryForm);
            if (!result.success) {
              invalid = true;
              setMemoryErrors((prev) => ({
                ...prev,
                [key]: result.error.issues,
              }));
            }
          }
        });
      }
      return !invalid;
    }),
  });

  const timelineMemories = useMemo(() => {
    return timeline?.memories.reduce((acc: Record<string, TimelineMemoryForm>, memory: Memory) => {
      acc[memory.id] = {
        id: memory.id,
        title: memory.title,
        name: memory.name,
        date: memory.date,
        content: memory.content,
        assets: memory.assets.map((asset) => asset.id),
        tags: memory.tags ? memory.tags.map((tag: Tag) => tag.id) : [],
        editable: memory.capabilities?.can_update ?? false,
      };
      return acc;
    }, {})}, [timeline]);

  const timelineAssets = useMemo(() => {
    return timeline?.memories.flatMap((memory: Memory) => memory.assets) || [];
  }, [timeline]);

  const {
    control,
    register,
    getValues,
    setValue,
    reset,
    watch,
    formState: {
      errors,
      isValid,
      isSubmitted,
      submitCount,
    },
    handleSubmit,
  } = useForm<TimelineForm>({
    defaultValues: {
      type: timeline?.type || 'human',
      lgcy_type: timeline?.lgcy_type || 'legacy',
      dedicated_to: timeline?.dedicated_to || '',
      allow_comments: timeline?.allow_comments ?? true,
      allow_reactions: timeline?.allow_reactions ?? true,
      header_image_id: timeline?.header_image?.id ?? null,
      memories: timelineMemories || [],
    },
    shouldUnregister: false,
    resolver: zodResolver(schemaTimeline) as any,
  });

  useFieldArray({
    control,
    name: 'memories',
  });

  const assets = useMemo(() => {
    return timelineAssets
      .concat(additionalAssets)
      .concat(
      files.map((file: (UppyFile<Record<string, unknown>, Record<string, unknown>> | Asset)) => {
      if (file.progress) {
        return {
          id: (file as UppyFile<Record<string, unknown>, Record<string, unknown>>).meta?.uuid || file.id,
          name: file.name,
          type: file.type,
          path: (file as UppyFile<Record<string, unknown>, Record<string, unknown>>).uploadURL ?? (file as Asset).path ?? '',
          size: file.size,
          thumbnail: (file as UppyFile<Record<string, unknown>, Record<string, unknown>>).preview as string ?? (file as Asset).thumbnail ?? '',
          progress: file.progress,
          error: file.error,
          status: (file as UppyFile<Record<string, unknown>, Record<string, unknown>>).response?.status ?? (file as Asset).status,
          local: true,
          original_date: (file as UppyFile<Record<string, unknown>, Record<string, unknown>>).meta?.original_date ? (file as UppyFile<Record<string, unknown>, Record<string, unknown>>).meta.original_date as string : undefined,
          uppyFileId: (file as UppyFile<Record<string, unknown>, Record<string, unknown>>).id,
        };
      } else {
        return {
          ...(file as unknown as Asset),
          local: false,
        };
      }
    })) as Asset[];
  }, [files, additionalAssets]);

  const formMemories = watch('memories');
  const lastUpdated = watch('lastUpdated');

  const { mutate: createTimeline, isPending: isCreatePending, isError: isCreateError } = useMutation({
    mutationFn: async (requestData: TimelineForm): Promise<Timeline> => {
      const response = await APIcreateTimeline(requestData);
      return response.data;
    },
    onError: (error) => {
      console.error(error);
    },
    onSuccess: (response: Timeline) => {
      useStore.setState({ notice: { type: 'timeline.created' } });
      reset();
      nav({ href: `/timelines/${response.id}` });
    },
  });

  const { mutate: updateTimeline, isPending: isUpdatePending, isError: isUpdateError } = useMutation({
    mutationFn: async (requestData: TimelineForm): Promise<Timeline> => {
      const response = await APIupdateTimeline(timeline.id, requestData);
      return response.data;
    },
    onError: (error) => {
      console.error(error);
    },
    onSuccess: (response: Timeline) => {
      useStore.setState({ notice: { type: 'timeline.updated' } });
      reset();
      nav({ href: `/timelines/${response.id}` });
    },
  });

  const isPending = isCreatePending || isUpdatePending;
  const isError = isCreateError || isUpdateError;

  const onSubmit = async (data: TimelineForm) => {
    data.memories = Object.values(formMemories);
    data.header_image_id = headerImageId;

    if (timeline) {
      updateTimeline(data);
    } else {
      createTimeline(data);
    }
  };

  useEffect(() => {
     if (isSubmitted && !isValid) {
      const firstErrorField = document.querySelector('.error-message');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [submitCount]);

  const setHoveredMemory = (id: string | null) => {
    if (id?.startsWith('memory-')) {
      setHoveredMemoryId(id);
    } else if (id?.startsWith('asset-')) {
      memories.forEach((memory) => {
        if (memory.assets.includes(id.replace('asset-', ''))) {
          setHoveredMemoryId(`memory-${memory.id}`);
        }
      });
    } else {
      setHoveredMemoryId(null);
    }
  };

  const moveAssetToMemory = (targetAssetId: string, targetMemoryId: string, position: number|undefined) => {
    const assetId = targetAssetId.startsWith('asset-') ? targetAssetId.replace('asset-', '') : '';
    const memoryId = targetMemoryId.startsWith('memory-') ? targetMemoryId.replace('memory-', '') : '';
    const sourceMemoryId = memories.find((memory) => memory.assets.includes(assetId))?.id ?? '';

    if (!assetId || !memoryId || !sourceMemoryId) return;

    memories.forEach((memory) => {
      const filteredAssets = memory.assets.filter((a) => a !== assetId);
      if (memory.assets.includes(assetId)) {
        setValue(`memories.${memory.id as unknown as number}.assets`, filteredAssets, { shouldDirty: true });
      }
      if (memory.id === memoryId) {
        const updatedAssets = position !== undefined ? [...filteredAssets.slice(0, position), assetId, ...filteredAssets.slice(position)] : [...filteredAssets, assetId];
        setValue(`memories.${memory.id as unknown as number}.assets`, updatedAssets, { shouldDirty: true });
      }
    });

    const sourceMemoryIndex = memories.findIndex((m) => m.id === sourceMemoryId);
    const targetMemoryIndex = memories.findIndex((m) => m.id === memoryId);

    if (sourceMemoryIndex === -1 || targetMemoryIndex === -1) return;

    const sourceMemory = memories[sourceMemoryIndex];
    const targetMemory = memories[targetMemoryIndex];

    const updatedMemories = [...memories];
    updatedMemories[sourceMemoryIndex] = {
      ...sourceMemory,
      assets: sourceMemory.assets.filter((a) => a !== assetId),
    };
    const targetMemoryAssets = targetMemory.assets.filter((a) => a !== assetId);
    updatedMemories[targetMemoryIndex] = {
      ...targetMemory,
      assets: position !== undefined ? [...targetMemoryAssets.slice(0, position), assetId, ...targetMemoryAssets.slice(position)] : [...targetMemoryAssets, assetId],
    };

    setMemories(updatedMemories);
  };

  const getAssetsForMemory = (assetIds: string[]) => {
    const memoryAssets: Asset[] = [];
    assetIds.forEach((id) => {
      const asset = assets.find((a) => String(a.id) === id);
      if (asset) {
        memoryAssets.push(asset);
      }
    });
    return memoryAssets;
  };

  const getNewMemory = (defaultValues?: Partial<TimelineMemoryForm>): TimelineMemoryForm => {
    return {
      id: `new-${Date.now()}`,
      title: '',
      name: profile?.name || '',
      date: '',
      content: '',
      assets: [],
      tags: [],
      editable: true,
      ...defaultValues,
    };
  };

  const removeAssetFromMemory = (assetId: string) => {
    const sourceMemoryId = memories.find((memory) => memory.assets.includes(assetId))?.id ?? '';
    const memory = formMemories ? Object.values(formMemories).find((m) => m.id === sourceMemoryId) : undefined;
    if (memory) {
      const updatedAssets = memory.assets.filter((a) => a !== assetId);
      setValue(`memories.${memory.id as unknown as number}.assets`, updatedAssets, { shouldDirty: true });
      setValue('lastUpdated', `${Date.now()}`);
    }
  };

  const addFile = (fileMeta: any) => {
    const date = fileMeta.metadata.original_date || '';
    const formMemories = getValues('memories');
    const memory = formMemories ? Object.values(formMemories).find((m) => m.date === date) : undefined;
    if (memory) {
      const updatedAssets = [...memory.assets, fileMeta.uuid];
      setValue(`memories.${memory.id as unknown as number}.assets`, updatedAssets, { shouldDirty: true });
    } else {
      const newMemory = getNewMemory({ assets: [fileMeta.uuid], date });
      setValue(`memories.${newMemory.id as unknown as number}`, newMemory, { shouldDirty: true });
    }
    setValue('lastUpdated', `${Date.now()}`);
  };

  const removeFile = (fileId: string) => {
    removeAssetFromMemory(fileId);
    APIdeleteAsset(fileId);
  };

  const splitFiles = (selectedAssetIds: string[]) => {
    selectedAssetIds.forEach((assetId) => {
      removeAssetFromMemory(assetId);
    });

    const newMemory = getNewMemory({ assets: selectedAssetIds });
    setValue(`memories.${newMemory.id as unknown as number}`, newMemory, { shouldDirty: true });

    setValue('lastUpdated', `${Date.now()}`);
  };

  const addMemories = (newMemories: Memory[]) => {
    newMemories.forEach((memory) => {
      setAdditionalAssets((prevAssets) => [...prevAssets, ...memory.assets]);
      const newMemory: TimelineMemoryForm = {
        id: memory.id,
        title: memory.title,
        name: memory.name,
        date: memory.date,
        content: memory.content,
        assets: memory.assets.map((asset) => asset.id),
        tags: memory.tags ? memory.tags.map((tag: Tag) => tag.id) : [],
        editable: memory.capabilities?.can_update ?? false,
      };
      setValue(`memories.${newMemory.id as unknown as number}`, newMemory, { shouldDirty: true });
    });
    setValue('lastUpdated', `${Date.now()}`);
  };

  useEffect(() => {
    if (formMemories) {
      const currentMemories = [...memories];
      Object.keys(formMemories).forEach((memoryId: string) => {
        const memory = formMemories[memoryId as keyof typeof formMemories] as Partial<TimelineMemoryForm>;
        const memoryIndex = currentMemories.findIndex((m) => m.id === memoryId);
        if (memoryIndex === -1) {
          currentMemories.push({
            id: memoryId,
            title: memory.title || '',
            name: memory.name || '',
            date: memory.date || '',
            content: memory.content || '',
            assets: memory.assets || [],
            tags: memory.tags || [],
            editable: memory.editable ?? false,
          });
        } else {
          Object.keys(memory).forEach((key) => {
            const typedKey = key as keyof TimelineMemoryForm;
            currentMemories[memoryIndex] = {
                ...currentMemories[memoryIndex],
                [typedKey]: memory[typedKey],
            };
          });
        }
      });
      setMemories(currentMemories);
    }
  }, [lastUpdated]);

  const sortedMemories = formMemories ?
    sortBy(Object.values(formMemories), (memory) => memory.date ?? '').filter((memory) => !memory.removed) :
    [];

  return (
    <div className="container timeline-create-container">
      {timeline ? (
        <div className="timeline-header wa-stack wa-gap-0 inner">
          <TimelineHeader
            timeline={timeline}
            mode="private"
            handleAddMemory={addMemories}
          />
        </div>
      ) : (
        <div className="page-header">
          <div className="page-eyebrow">
            Memory maker
          </div>
          <h2 className="page-title">
            What <em>memory</em> would you like to start with?
          </h2>
        </div>
      )}
      <div className="wa-flank wa-flank:end wa-gap-0 wa-align-items-start" style={{ '--flank-size': '450px' } as React.CSSProperties}>
        <div className="timeline-form">
          {timeline?.contributor && (
            <TimelineContributorNotice />
          )}
          {isError && (
            <wa-callout variant="danger">
              <i className="fa-regular fa-circle-exclamation" slot="icon" />
              <strong>An error occurred while {timeline ? 'updating' : 'creating'} the memory.</strong>
            </wa-callout>
          )}
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <input type="hidden" {...register('lastUpdated')} />
            {(timeline?.capabilities?.can_update || timeline?.header_image) && (
              <TimelineCreateHeaderImage
                asset={timeline?.header_image?.id === headerImageId ? timeline.header_image : null}
                canEdit={timeline?.capabilities?.can_update}
                onChange={setHeaderImageId}
                setUploading={setHeaderImageUploading}
              />
            )}
            <div className={classnames('timeline-form-type', { 'hidden': timeline?.capabilities?.can_update === false })}>
              <TimelineCreateVariantSelect />
              <MemoryTypeSelect
                variant="timeline"
                selectedType={watch('type')}
                register={register}
              />
              <MemoryLgcyTypeSelect
                selectedType={watch('lgcy_type')}
                errors={errors}
                register={register}
              />
              <div className="timeline-title-container inner">
                <label>Timeline title</label>
                <wa-input
                  type="text"
                  {...register('dedicated_to')}
                  placeholder="Who is this timeline for?"
                  className={errors.dedicated_to ? 'invalid' : ''}
                  hint={errors.dedicated_to ? errors.dedicated_to.message : ''}
                />
              </div>
              <MemoryEngagement
                variant="timeline"
                allowComments={watch('allow_comments')}
                allowReactions={watch('allow_reactions')}
                register={register}
                setValue={setValue}
              />
            </div>
            <TimelineCreateFiles
              addFile={addFile}
              removeFile={removeFile}
              setUploading={setUploading}
              setFiles={(newFiles: UppyFile<Record<string, unknown>, Record<string, unknown>>[]) => {
                setFiles([...files, ...newFiles]);
              }}
            >
              <DragDropProvider
                onDragStart={(event) => {
                  sourceParentRef.current = event.operation.source?.element?.parentElement ?? null;
                  const sourceId = event.operation.source?.id;
                  setHoveredMemory(sourceId?.toString() ?? null);
                }}
                onDragOver={(event) => {
                  const targetId = event.operation.target?.id;
                  if (event.operation.target?.type === 'column' && event.operation.target?.isDropTarget) {
                    setHoveredMemory(targetId?.toString() ?? null);
                  }
                }}
                onDragEnd={(event) => {
                  const sourceElement = event.operation.source?.element;
                  const prevParent = sourceParentRef.current;
                  sourceParentRef.current = null;
                  if (
                    sourceElement &&
                    prevParent &&
                    sourceElement.parentElement !== prevParent
                  ) {
                    prevParent.appendChild(sourceElement);
                  }

                  if (event.canceled) return;

                  const sourceId = event.operation.source?.id;
                  const targetIndex = (event.operation.target as { index?: number } | null)?.index;
                  moveAssetToMemory(sourceId?.toString() ?? '', hoveredMemoryId ?? '', targetIndex);
                  setHoveredMemory(null);
                }}
              >
                {sortedMemories.map((memory, index) => (
                  <TimelineCreateMemory
                    key={memory.id}
                    index={index}
                    memory={memory}
                    assets={getAssetsForMemory(memory.assets)}
                    hovered={`memory-${memory.id}` === hoveredMemoryId}
                    setValue={setValue}
                    errors={memoryErrors[memory.id] ?? []}
                    handleRemoveFile={removeFile}
                    handleSplitFiles={splitFiles}
                  />
                ))}
              </DragDropProvider>
            </TimelineCreateFiles>
            {(memories.length > 0 || timeline) && (
              <div className="wa-cluster timeline-form-actions">
                <wa-button
                  type="button"
                  className="hidden"
                  appearance="outlined"
                  disabled={isPending || uploading}
                >
                  Save draft
                </wa-button>
                <wa-button
                  type="submit"
                  disabled={isPending || uploading || headerImageUploading}
                >
                  {isPending ? (
                    <>
                      <wa-spinner slot="start" />
                      Saving...
                    </>
                  ) : (
                    <>
                      {timeline ? 'Update Timeline' : 'Publish Timeline'}
                    </>
                  )}
                </wa-button>
              </div>
            )}
          </form>
        </div>
        <div className={`${timeline ? 'timeline-view-sidebar' : 'timeline-info'} inner`}>
          <StickyBox offsetTop={20}>
            {timeline ? (
              <>
                <TimelineButtons timeline={timeline} page="edit" />
                <MemoryTimeline timeline={timeline} mode="private" />
                <TimelineContributors timeline={timeline} />
              </>
            ) : (
              <TimelineCreateInfo />
            )}
          </StickyBox>
        </div>
      </div>
    </div>
  );
}

export default TimelineCreate;
